import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Download,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  SkipForward,
  X,
} from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getInvoiceDraftCsvUrl,
  listInvoiceDrafts,
  markInvoiceDraftExported,
  skipInvoiceDraft,
  type InvoiceDraft,
} from '@/features/planning/api';
import type { Language } from '@/types';
import { cn } from '@/lib/utils';

interface InvoicingWorkspaceProps {
  language: Language;
}

const copy = {
  en: {
    title: 'Invoicing',
    description: '',
    adminOnly: 'Only admins can view invoicing.',
    prev: 'Previous',
    next: 'Next',
    refresh: 'Refresh',
    noDrafts: 'No drafts for this month.',
    statusPending: 'Pending',
    statusExported: 'Exported',
    statusSkipped: 'Skipped',
    markExported: 'Mark exported',
    skip: 'Skip',
    csv: 'Download CSV',
    fortnoxNumber: 'Fortnox invoice #',
    note: 'Note',
    base: 'Base',
    extras: 'Extras',
    total: 'Total',
    customer: 'Customer',
    missingPrice: 'Price missing',
    expand: 'Show details',
    collapse: 'Hide details',
    actions: 'Actions',
    kpiSites: 'Sites',
    kpiPending: 'Pending',
    kpiExported: 'Exported',
    kpiTotal: 'Total invoiced',
    columnSite: 'Site',
    columnStatus: 'Status',
    columnBase: 'Base',
    columnExtras: 'Extras',
    columnTotal: 'Total',
    skipReason: 'Enter reason for skip',
    lines: 'Lines',
    filterSite: 'BRF',
    filterStatus: 'Status',
    all: 'All',
    searchPlaceholder: 'Search by name or customer #',
    clearFilters: 'Clear',
    showingOf: (x: number, y: number) => `Showing ${x} of ${y}`,
    noMatch: 'No BRFs match the current filter.',
    noCustomer: 'No customer #',
    pendingExtras: (n: number) => `${n} unapproved extra${n === 1 ? '' : 's'}`,
  },
  sv: {
    title: 'Fakturering',
    description: '',
    adminOnly: 'Endast admin kan se fakturering.',
    prev: 'Föregående',
    next: 'Nästa',
    refresh: 'Uppdatera',
    noDrafts: 'Inga utkast för denna månad.',
    statusPending: 'Väntar',
    statusExported: 'Exporterad',
    statusSkipped: 'Överhoppad',
    markExported: 'Markera exporterad',
    skip: 'Hoppa över',
    csv: 'Ladda ner CSV',
    fortnoxNumber: 'Fortnox fakturanr',
    note: 'Anteckning',
    base: 'Bas',
    extras: 'Extra',
    total: 'Totalt',
    customer: 'Kundnr',
    missingPrice: 'Pris saknas',
    expand: 'Visa detaljer',
    collapse: 'Dölj detaljer',
    actions: 'Åtgärder',
    kpiSites: 'Objekt',
    kpiPending: 'Väntar',
    kpiExported: 'Exporterad',
    kpiTotal: 'Summa faktureras',
    columnSite: 'Objekt',
    columnStatus: 'Status',
    columnBase: 'Bas',
    columnExtras: 'Extra',
    columnTotal: 'Totalt',
    skipReason: 'Ange anledning för överhoppning',
    lines: 'Rader',
    filterSite: 'BRF',
    filterStatus: 'Status',
    all: 'Alla',
    searchPlaceholder: 'Sök på namn eller kundnr',
    clearFilters: 'Rensa',
    showingOf: (x: number, y: number) => `Visar ${x} av ${y}`,
    noMatch: 'Inga BRF:er matchar filtret.',
    noCustomer: 'Saknar kundnr',
    pendingExtras: (n: number) => `${n} extra ej godkän${n === 1 ? 'd' : 'da'}`,
  },
} as const;

const MONTH_NAMES_SV = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const MONTH_NAMES_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function currentYearMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function shift(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function formatSek(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(value);
}

type StatusVariant = 'success' | 'warning' | 'default' | 'destructive';
type InvoicingCopy = typeof copy[keyof typeof copy];

function statusMeta(status: InvoiceDraft['status'], text: InvoicingCopy): { label: string; variant: StatusVariant } {
  if (status === 'exported') return { label: text.statusExported, variant: 'success' };
  if (status === 'skipped') return { label: text.statusSkipped, variant: 'default' };
  return { label: text.statusPending, variant: 'warning' };
}

function hasPlaceholderPrice(draft: InvoiceDraft): boolean {
  if ((draft.total_inc_moms ?? 0) > 0) return false;
  return draft.lines.some((line) => line.line_type === 'recurring' && (line.price_ex_moms ?? 0) === 0);
}

export function InvoicingWorkspace({ language }: InvoicingWorkspaceProps) {
  const { session, profile } = useAuth();
  const text = copy[language];
  const token = session?.access_token ?? null;
  const isAdmin = profile?.role === 'admin';

  const { year: initY, month: initM } = currentYearMonth();
  const [year, setYear] = useState(initY);
  const [month, setMonth] = useState(initM);
  const [drafts, setDrafts] = useState<InvoiceDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [exportForm, setExportForm] = useState<Record<string, { fortnoxNumber: string; note: string }>>({});
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [siteOpen, setSiteOpen] = useState(false);

  const monthLabel = (language === 'sv' ? MONTH_NAMES_SV : MONTH_NAMES_EN)[month - 1];

  const loadDrafts = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await listInvoiceDrafts(year, month, token);
      setDrafts(res);
    } catch (err) {
      console.error('Failed to load drafts', err);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, year, month, isAdmin]);

  const draftKey = (d: InvoiceDraft) => `${d.site_id}-${d.period_year}-${d.period_month}`;

  const handleMarkExported = async (draft: InvoiceDraft) => {
    if (!token) return;
    const key = draftKey(draft);
    const form = exportForm[key] ?? { fortnoxNumber: '', note: '' };
    setExportingId(key);
    try {
      await markInvoiceDraftExported(
        {
          siteId: draft.site_id,
          year: draft.period_year,
          month: draft.period_month,
          fortnoxInvoiceNumber: form.fortnoxNumber.trim() || null,
          note: form.note.trim() || null,
        },
        token,
      );
      setExportForm((prev) => ({ ...prev, [key]: { fortnoxNumber: '', note: '' } }));
      setExpandedId(null);
      await loadDrafts();
    } catch (err) {
      console.error('Mark exported failed', err);
    } finally {
      setExportingId(null);
    }
  };

  const handleSkip = async (draft: InvoiceDraft) => {
    if (!token) return;
    const note = window.prompt(text.skipReason);
    if (!note?.trim()) return;
    try {
      await skipInvoiceDraft(
        {
          siteId: draft.site_id,
          year: draft.period_year,
          month: draft.period_month,
          note: note.trim(),
        },
        token,
      );
      await loadDrafts();
    } catch (err) {
      console.error('Skip failed', err);
    }
  };

  const handleCsvDownload = async (draft: InvoiceDraft) => {
    if (!token || !draft.id) return;
    try {
      const res = await fetch(getInvoiceDraftCsvUrl(draft.site_id, year, month), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`csv fetch failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${draft.site_name}-${year}-${String(month).padStart(2, '0')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed', err);
    }
  };

  const kpi = useMemo(() => ({
    sites: drafts.length,
    pending: drafts.filter((d) => d.status === 'pending').length,
    exported: drafts.filter((d) => d.status === 'exported').length,
    total: drafts.reduce((sum, d) => sum + (d.total_inc_moms ?? 0), 0),
  }), [drafts]);

  const filteredDrafts = useMemo(() => {
    return drafts.filter((d) => {
      if (siteFilter !== 'all' && d.site_id !== siteFilter) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      return true;
    });
  }, [drafts, siteFilter, statusFilter]);

  const filterActive = siteFilter !== 'all' || statusFilter !== 'all';
  const selectedSite = drafts.find((d) => d.site_id === siteFilter);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{text.adminOnly}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{text.title}</h1>
          {text.description && <p className="text-sm text-muted-foreground">{text.description}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-md border bg-background">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-none rounded-l-md"
              onClick={() => { const n = shift(year, month, -1); setYear(n.year); setMonth(n.month); }}
              aria-label={text.prev}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[140px] text-center text-sm font-semibold px-2">
              {monthLabel} {year}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-none rounded-r-md"
              onClick={() => { const n = shift(year, month, 1); setYear(n.year); setMonth(n.month); }}
              aria-label={text.next}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={loadDrafts} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">{text.refresh}</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label={text.kpiSites} value={String(kpi.sites)} />
        <KpiTile label={text.kpiPending} value={String(kpi.pending)} tone="warning" />
        <KpiTile label={text.kpiExported} value={String(kpi.exported)} tone="success" />
        <KpiTile label={text.kpiTotal} value={formatSek(kpi.total)} emphasize />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1 max-w-[360px]">
          <label className="text-xs text-muted-foreground">{text.filterSite}</label>
          <Popover open={siteOpen} onOpenChange={setSiteOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" aria-expanded={siteOpen} className="w-full justify-between font-normal">
                <span className="truncate">
                  {siteFilter === 'all' ? text.all : (
                    <span className="inline-flex items-center gap-2">
                      <span className="font-medium">{selectedSite?.site_name ?? '—'}</span>
                      <span className="h-4 w-px bg-border" aria-hidden />
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {selectedSite?.fortnox_customer_number ?? text.noCustomer}
                      </span>
                    </span>
                  )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-0" align="start">
              <Command
                filter={(value, search) => {
                  if (!search) return 1;
                  return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                }}
              >
                <CommandInput placeholder={text.searchPlaceholder} />
                <CommandList>
                  <CommandEmpty>—</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setSiteFilter('all'); setSiteOpen(false); }}>
                      <Check className={cn('mr-2 h-4 w-4', siteFilter === 'all' ? 'opacity-100' : 'opacity-0')} />
                      {text.all}
                    </CommandItem>
                    {drafts.map((d) => {
                      const customer = d.fortnox_customer_number ?? '';
                      const searchValue = `${d.site_name} ${customer}`.trim();
                      return (
                        <CommandItem
                          key={d.site_id}
                          value={searchValue}
                          onSelect={() => { setSiteFilter(d.site_id); setSiteOpen(false); }}
                        >
                          <Check className={cn('mr-2 h-4 w-4', siteFilter === d.site_id ? 'opacity-100' : 'opacity-0')} />
                          <span className="flex-1 truncate">{d.site_name}</span>
                          <span className="mx-2 h-4 w-px bg-border shrink-0" aria-hidden />
                          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                            {customer || text.noCustomer}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="min-w-[160px]">
          <label className="text-xs text-muted-foreground">{text.filterStatus}</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{text.all}</SelectItem>
              <SelectItem value="pending">{text.statusPending}</SelectItem>
              <SelectItem value="exported">{text.statusExported}</SelectItem>
              <SelectItem value="skipped">{text.statusSkipped}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filterActive && (
          <div className="flex items-center gap-3 pb-[2px] ml-auto">
            <span className="text-xs text-muted-foreground tabular-nums">
              {text.showingOf(filteredDrafts.length, drafts.length)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSiteFilter('all'); setStatusFilter('all'); }}
            >
              <X className="h-4 w-4 mr-1" />
              {text.clearFilters}
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : drafts.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {text.noDrafts}
          </CardContent>
        </Card>
      ) : filteredDrafts.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {text.noMatch}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="hidden md:grid grid-cols-[1fr_120px_120px_120px_140px_80px] items-center gap-3 px-4 py-2 border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            <div>{text.columnSite}</div>
            <div>{text.columnStatus}</div>
            <div className="text-right">{text.columnBase}</div>
            <div className="text-right">{text.columnExtras}</div>
            <div className="text-right">{text.columnTotal}</div>
            <div className="text-right">{text.actions}</div>
          </div>
          <div className="divide-y">
            {filteredDrafts.map((draft) => {
              const key = draftKey(draft);
              const expanded = expandedId === key;
              const sm = statusMeta(draft.status, text);
              const placeholder = hasPlaceholderPrice(draft);
              const form = exportForm[key] ?? { fortnoxNumber: '', note: '' };
              return (
                <div key={key} className={cn('group', expanded && 'bg-muted/20')}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : key)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/30 transition-colors"
                  >
                    <div className="grid md:grid-cols-[1fr_120px_120px_120px_140px_80px] gap-3 items-center">
                      <div className="flex items-center gap-2 min-w-0">
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                            expanded ? 'rotate-0' : '-rotate-90',
                          )}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="font-semibold truncate">{draft.site_name}</span>
                            <span className="h-4 w-px bg-border shrink-0" aria-hidden />
                            <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                              {draft.fortnox_customer_number ?? text.noCustomer}
                            </span>
                          </div>
                          {draft.fortnox_invoice_number && (
                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                              #{draft.fortnox_invoice_number}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant={sm.variant}>{sm.label}</Badge>
                        {placeholder && (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {text.missingPrice}
                          </Badge>
                        )}
                        {draft.pending_extras_count > 0 && (
                          <Badge variant="warning" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {text.pendingExtras(draft.pending_extras_count)}
                          </Badge>
                        )}
                      </div>

                      <div className="text-right text-sm tabular-nums text-muted-foreground">
                        {formatSek(draft.base_amount_ex_moms)}
                      </div>
                      <div className="text-right text-sm tabular-nums text-muted-foreground">
                        {formatSek(draft.extras_amount_ex_moms)}
                      </div>
                      <div className="text-right text-sm font-semibold tabular-nums">
                        {formatSek(draft.total_inc_moms)}
                      </div>

                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={!draft.id || !token}
                              onSelect={() => handleCsvDownload(draft)}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {text.csv}
                            </DropdownMenuItem>
                            {draft.status === 'pending' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={() => handleSkip(draft)}>
                                  <SkipForward className="h-4 w-4 mr-2" />
                                  {text.skip}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-4 pt-1 space-y-4">
                      {draft.admin_note && (
                        <div className="text-xs text-muted-foreground italic">{draft.admin_note}</div>
                      )}

                      {draft.lines.length > 0 && (
                        <div className="border rounded-md overflow-hidden">
                          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground bg-muted/40 border-b">
                            {text.lines}
                          </div>
                          <table className="w-full text-sm">
                            <tbody>
                              {draft.lines.map((line) => {
                                const isZero = (line.price_ex_moms ?? 0) === 0 && line.line_type === 'recurring';
                                const isPendingExtra = line.line_type === 'extra' && /ej godkänd/i.test(line.description ?? '');
                                return (
                                  <tr key={line.id} className="border-b last:border-b-0">
                                    <td className="px-3 py-2">
                                      <div className="font-medium flex items-center gap-2">
                                        {line.description}
                                        {isZero && (
                                          <Badge variant="destructive" className="gap-1">
                                            <AlertTriangle className="h-3 w-3" />
                                            {text.missingPrice}
                                          </Badge>
                                        )}
                                        {isPendingExtra && (
                                          <Badge variant="warning" className="gap-1">
                                            <AlertTriangle className="h-3 w-3" />
                                            {text.pendingExtras(1)}
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-xs text-muted-foreground">{line.line_type}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                                      {formatSek(line.price_ex_moms)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {draft.status === 'pending' && (
                        <div className="grid gap-3 md:grid-cols-[220px_1fr_auto] md:items-end">
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">{text.fortnoxNumber}</label>
                            <Input
                              value={form.fortnoxNumber}
                              onChange={(e) => setExportForm((prev) => ({ ...prev, [key]: { ...form, fortnoxNumber: e.target.value } }))}
                              placeholder="2026-0042"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">{text.note}</label>
                            <Textarea
                              value={form.note}
                              onChange={(e) => setExportForm((prev) => ({ ...prev, [key]: { ...form, note: e.target.value } }))}
                              rows={1}
                              className="min-h-[40px] resize-none"
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleMarkExported(draft)}
                            disabled={exportingId === key}
                          >
                            {exportingId === key ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4" />
                            )}
                            <span className="ml-1.5">{text.markExported}</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

interface KpiTileProps {
  label: string;
  value: string;
  tone?: 'warning' | 'success';
  emphasize?: boolean;
}

function KpiTile({ label, value, tone, emphasize }: KpiTileProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
        <div
          className={cn(
            'mt-1 font-bold tabular-nums',
            emphasize ? 'text-2xl' : 'text-2xl',
            tone === 'warning' && 'text-accent-foreground',
            tone === 'success' && 'text-chart-4',
          )}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
